import { format } from "date-fns";
import Cal, { getCalApi } from "@calcom/embed-react";
import { useEffect, useState } from "react";
import Card from "../../ui/Card/Card";
import styles from "./Meeting.module.scss";
import tseringImage from "./tsering.png";
import Badge from "../../ui/Badge/Badge";
import Button from "../../ui/Button/Button";
import useUser from "../../providers/UserProvider/useUser";
import { Calendar, CheckCircle } from "../../ui/Icon/Icon";
import classNames from "classnames";
import { usePostHog } from "posthog-js/react";

const calendarHeightLarge = 570;
const calendarHeightSmall = 542;

function Meeting({ onSkip }: { onSkip: () => void }) {
  const posthog = usePostHog();
  const { user } = useUser();
  const [bookingDetails, setBookingDetails] = useState<{
    date: Date;
  }>();

  const [isCalendarStep, setIsCalendarStep] = useState(true);

  const handleSkip = () => {
    posthog.capture("Meeting Skipped");

    onSkip();
  };

  useEffect(() => {
    async function initCalendar() {
      const cal = await getCalApi({ namespace: "macaw" });

      cal("on", {
        action: "__dimensionChanged",
        callback: (e) => {
          if (
            "iframeHeight" in e.detail.data &&
            typeof e.detail.data.iframeHeight === "number"
          ) {
            setIsCalendarStep(
              e.detail.data.iframeHeight === calendarHeightLarge ||
                e.detail.data.iframeHeight === calendarHeightSmall,
            );
          }
        },
      });

      cal("on", {
        action: "bookingSuccessful",
        callback: ({ detail }) => {
          if ("date" in detail.data && typeof detail.data.date === "string") {
            posthog.capture("Meeting Booked", {
              date: detail.data.date,
            });

            setBookingDetails({
              date: new Date(detail.data.date),
            });
          }
        },
      });

      cal("ui", {
        theme: "light",
        styles: {
          branding: { brandColor: "#000000" },
        },
        hideEventTypeDetails: true,
        layout: "month_view",
      });
    }

    void initCalendar();
  }, [posthog]);

  return (
    <Card
      className={classNames(styles.card, {
        [styles.cardLarge]: isCalendarStep,
      })}
    >
      <div className={styles.cardHeader}>
        <span>Book a 15-minute intro call with</span>
        <Badge showDot={false}>
          <div className={styles.badge}>
            <img className={styles.tseringImage} src={tseringImage} alt="" />
            Tsering
          </div>
        </Badge>
      </div>
      <div className={styles.cardBody}>
        {bookingDetails ? (
          <>
            <div className={styles.bookingSuccessful}>
              <CheckCircle className={styles.checkIcon} size="large" />
              <p className={styles.bookingSuccessfulHeading}>
                Introduction call scheduled
              </p>
              <p className={styles.bookingSuccessfulText}>
                We sent an email with a calendar invitation
              </p>
              <p className={styles.bookingSuccessfulDate}>
                <Calendar />
                <span>{format(bookingDetails.date, "EEEE, MMMM d, p")} </span>
              </p>
            </div>
            <Button
              variant="primary"
              className={styles.continueButton}
              onClick={handleSkip}
            >
              Continue to Macaw
            </Button>
          </>
        ) : (
          <>
            <p className={styles.cardText}>
              To get the most out of Macaw we offer a free introduction call to
              walk you through the platform and answer any questions you have.
              This is optional, but highly recommended!
            </p>
            <Cal
              namespace="macaw"
              calLink="tsering/macaw"
              style={{
                width: isCalendarStep ? "1026px" : "auto",
                height: isCalendarStep ? `490px` : "450px",
                overflow: "hidden",
                margin: "0 -118px",
              }}
              config={{
                layout: "month_view",
                theme: "light",
                firstName: user?.firstName ?? "",
                lastName: user?.lastName ?? "",
                email: user?.email ?? "",
              }}
            />
            <div className={styles.skipButton}>
              <Button variant="outline" onClick={handleSkip}>
                Skip
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

export default Meeting;
